import { useSyncExternalStore, type ReactNode } from 'react';

export type ExclusiveWorkspaceRoute = 'scene' | 'recognition' | 'mesh-studio' | 'event-location' | 'buildings';

export function resolveExclusiveWorkspaceRoute(pathname: string): ExclusiveWorkspaceRoute {
  if (pathname === '/buildings' || pathname === '/buildings/') return 'buildings';
  if (pathname === '/recognition-arm' || pathname === '/recognition-arm/') return 'recognition';
  if (pathname === '/event-location' || pathname === '/event-location/') return 'event-location';
  if (pathname === '/mesh-studio' || pathname === '/mesh-studio/') return 'mesh-studio';
  return 'scene';
}

const routeSubscribers = new Set<() => void>();
let uninstallNavigationOwner: (() => void) | null = null;

function installNavigationOwner() {
  if (uninstallNavigationOwner || typeof window === 'undefined') return;
  const pushState = window.history.pushState;
  const replaceState = window.history.replaceState;
  const publish = () => routeSubscribers.forEach((subscriber) => subscriber());
  window.history.pushState = function (...args) { pushState.apply(this, args); publish(); };
  window.history.replaceState = function (...args) { replaceState.apply(this, args); publish(); };
  window.addEventListener('popstate', publish);
  uninstallNavigationOwner = () => {
    window.history.pushState = pushState;
    window.history.replaceState = replaceState;
    window.removeEventListener('popstate', publish);
    uninstallNavigationOwner = null;
  };
}

function subscribeRoute(subscriber: () => void) {
  routeSubscribers.add(subscriber);
  installNavigationOwner();
  return () => {
    routeSubscribers.delete(subscriber);
    if (!routeSubscribers.size) uninstallNavigationOwner?.();
  };
}

function currentRoute() { return resolveExclusiveWorkspaceRoute(window.location.pathname); }

export function ExclusiveWorkspaceRouteOwner(props: Omit<Parameters<typeof ExclusiveWorkspaceSurface>[0], 'route'>) {
  const route = useSyncExternalStore(subscribeRoute, currentRoute, (): ExclusiveWorkspaceRoute => 'scene');
  return <ExclusiveWorkspaceSurface route={route} {...props} />;
}

export function ExclusiveWorkspaceSurface({
  route, scene, recognition, meshStudio, eventLocation, buildings,
}: {
  route: ExclusiveWorkspaceRoute;
  scene: ReactNode;
  recognition: ReactNode;
  meshStudio: ReactNode;
  eventLocation?: ReactNode;
  buildings?: ReactNode;
}) {
  const surface = route === 'buildings' ? buildings ?? <div role="alert">BUILDINGS_WORKSPACE_UNAVAILABLE</div> : route === 'event-location' ? eventLocation ?? <div role="alert">EVENT_WORKSPACE_UNAVAILABLE</div> : route === 'recognition' ? recognition : route === 'mesh-studio' ? meshStudio : scene;
  return <div data-testid="exclusive-workspace-surface" data-workspace-route={route}>{surface}</div>;
}
